import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError, track } from "@/lib/api";

const GROUPS = [
  { value: "products", title: "Our products", desc: "Book a demo or talk to sales about a product." },
  { value: "Partnership", title: "Investors & partners", desc: "Connect with Sentinel Nexus Technologies." },
  { value: "Careers", title: "Careers", desc: "Tell us where you can make an impact." },
];
const groupOf = (interest) => (interest === "Partnership" || interest === "Careers" ? interest : "products");

const Field = ({ id, label, optional, as: Tag = "input", ...props }) => (
  <div className="field">
    <label htmlFor={`briefing-${id}`} className="field-label">
      {label} {optional && <span className="normal-case tracking-normal text-nx-dim">(optional)</span>}
    </label>
    <Tag id={`briefing-${id}`} name={id} className="nx-input" data-testid={`briefing-input-${id}`} {...props} />
  </div>
);

const ProductChoice = ({ products, value, onChange }) => (
  <fieldset className="mt-6" data-testid="briefing-product-picker">
    <legend className="field-label mb-3">Which product?</legend>
    <div className="flex flex-wrap gap-2">
      {products.map((p) => (
        <label key={p.slug} className="product-chip" data-testid={`briefing-interest-${p.slug}`}>
          <input type="radio" name="product" value={p.name} checked={value === p.name} onChange={() => onChange(p.name)} className="sr-only" />
          <span>{p.name}{p.status !== "Live" && <em> · {p.status}</em>}</span>
        </label>
      ))}
    </div>
  </fieldset>
);

const PathPicker = ({ value, onChange, products }) => {
  const group = groupOf(value);
  const pick = (g) => onChange(g === "products" ? products[0]?.name || "BuiltOS" : g);
  return (
    <>
      <fieldset>
        <legend className="field-label mb-4">What brings you here?</legend>
        <div className="path-grid">
          {GROUPS.map((g) => (
            <label key={g.value} className="path-card" data-testid={g.value === "products" ? "briefing-path-products" : `briefing-interest-${g.value.toLowerCase()}`}>
              <input type="radio" name="path" value={g.value} checked={group === g.value} onChange={() => pick(g.value)} className="sr-only" />
              <span className="path-body">
                <span className="path-title">{g.title}</span>
                <span className="path-desc">{g.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {group === "products" && <ProductChoice products={products} value={value} onChange={onChange} />}
    </>
  );
};

const Success = ({ c }) => {
  const ref = useRef(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <div ref={ref} tabIndex={-1} role="status" className="form-card success-card" data-testid="briefing-success-card">
      <CheckCircle2 className="h-8 w-8 text-nx-cyan" aria-hidden="true" />
      <h3 className="mt-6 text-2xl font-semibold tracking-tight text-nx-text">{c.success_title}</h3>
      <p className="mt-3 text-nx-muted">{c.success_body}</p>
    </div>
  );
};

export const ContactForm = ({ c, interest, setInterest, products }) => {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const startedAt = useRef(Date.now());

  async function onSubmit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = Object.fromEntries(["name", "email", "company", "role", "message", "website"].map((k) => [k, fd.get(k) || ""]));
    setStatus("sending");
    setError("");
    try {
      await api.post("/leads", { ...body, interest, elapsed_ms: Date.now() - startedAt.current });
      track("form_submit", { cta: `briefing-${interest.toLowerCase()}` });
      setStatus("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setStatus("idle");
    }
  }

  if (status === "done") return <Success c={c} />;
  const sending = status === "sending";
  const careers = interest === "Careers";
  return (
    <form onSubmit={onSubmit} className="form-card" data-testid="briefing-form" aria-describedby="briefing-privacy">
      <PathPicker value={interest} onChange={setInterest} products={products} />
      <div className="mt-8 grid gap-x-5 sm:grid-cols-2">
        <Field id="name" label="Name" required maxLength={120} autoComplete="name" />
        <Field id="email" label="Email" type="email" required maxLength={200} autoComplete="email" />
        <Field id="company" label={careers ? "Current company" : "Company or fund"} required maxLength={160} autoComplete="organization" />
        <Field id="role" label={careers ? "Role you are interested in" : "Your role"} optional maxLength={120} autoComplete="organization-title" />
      </div>
      <Field id="message" label="Message" optional as="textarea" rows={4} maxLength={2000}
        placeholder={careers ? "Include a link to your CV, LinkedIn or portfolio." : "Tell us what your organisation needs."} />
      <div className="hp" aria-hidden="true">
        <label htmlFor="website">Leave this field empty</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      {error && <p role="alert" className="form-error" data-testid="briefing-error">{error}</p>}
      <button type="submit" disabled={sending} className="btn btn-strong" data-testid="briefing-submit-button">
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
        {sending ? "Sending" : "Send enquiry"}
        {!sending && <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
      </button>
      <p id="briefing-privacy" className="mt-4 text-xs text-nx-dim">We use these details only to reply to your enquiry.</p>
    </form>
  );
};
